import Feature from 'ol/Feature';
import GeometryCollection from 'ol/geom/GeometryCollection';
import MultiLineString from 'ol/geom/MultiLineString';
import MultiPolygon from 'ol/geom/MultiPolygon';
import Polygon from 'ol/geom/Polygon';
import type Geometry from 'ol/geom/Geometry';
import type { Coordinate } from 'ol/coordinate';
import RBush from 'ol/structs/RBush';
import type { EntityViewLevel, LayerModel } from '../core/types';
import { entityPresentationGeometry, entityViewEntities } from '../domain/entityView';

export type BoundaryLevel = 'finca' | 'suerte' | 'tablon';
const presentationFeatureCache = new WeakMap<LayerModel, Map<string, readonly Feature<Geometry>[]>>();

const polygonsFrom = (geometry: Geometry | undefined): Polygon[] => {
  if (geometry instanceof Polygon) return [geometry];
  if (geometry instanceof MultiPolygon) return geometry.getPolygons();
  if (geometry instanceof GeometryCollection) return geometry.getGeometries().flatMap(polygonsFrom);
  return [];
};

export function presentationBoundary(geometries: readonly Geometry[]): MultiLineString {
  const polygons = geometries.flatMap(polygonsFrom);
  const polygonIndex = new RBush<Polygon>();
  polygonIndex.load(polygons.map((polygon) => polygon.getExtent()), polygons);
  const inside = (coordinate: Coordinate): boolean => polygonIndex
    .getInExtent([coordinate[0] ?? 0, coordinate[1] ?? 0, coordinate[0] ?? 0, coordinate[1] ?? 0])
    .some((candidate) => candidate.intersectsCoordinate(coordinate));
  const boundarySegments: [Coordinate, Coordinate][] = [];
  polygons.forEach((polygon) => {
    polygon.getCoordinates().forEach((ring) => {
      for (let index = 1; index < ring.length; index += 1) {
        const start = ring[index - 1];
        const end = ring[index];
        if (!start || !end) continue;
        const startX = start[0];
        const startY = start[1];
        const endX = end[0];
        const endY = end[1];
        if (startX === undefined || startY === undefined || endX === undefined || endY === undefined) continue;
        const dx = endX - startX;
        const dy = endY - startY;
        const length = Math.hypot(dx, dy);
        if (length === 0) continue;
        const offset = Math.min(0.5, Math.max(0.05, length * 0.001));
        const midpoint: Coordinate = [(startX + endX) / 2, (startY + endY) / 2];
        const normal: Coordinate = [(-dy / length) * offset, (dx / length) * offset];
        const midpointX = midpoint[0] ?? 0;
        const midpointY = midpoint[1] ?? 0;
        const normalX = normal[0] ?? 0;
        const normalY = normal[1] ?? 0;
        const left: Coordinate = [midpointX + normalX, midpointY + normalY];
        const right: Coordinate = [midpointX - normalX, midpointY - normalY];
        const leftInside = inside(left);
        const rightInside = inside(right);
        if (!(leftInside && rightInside)) boundarySegments.push([start, end]);
      }
    });
  });
  return new MultiLineString(boundarySegments);
}

export function buildPresentationBoundaries(model: LayerModel, level: BoundaryLevel, allowedFragmentIds?: ReadonlySet<string>): Feature<MultiLineString>[] {
  return entityViewEntities(model, level, allowedFragmentIds).flatMap((entity) => {
    if (level === 'suerte' && entity.id.includes('/suerte:__sin_suerte_')) return [];
    if (level === 'tablon' && entity.id.endsWith('/tablon:__sin_tablon__')) return [];
    const fragmentIds = entity.fragmentIds;
    const geometries = fragmentIds.flatMap((id) => {
      const geometry = model.fragmentsById.get(id)?.feature.getGeometry();
      return geometry ? [geometry] : [];
    });
    const boundary = presentationBoundary(geometries);
    const suerte = level === 'suerte' ? model.suertes.get(entity.id) : level === 'tablon' ? model.suertes.get(model.tablones.get(entity.id)?.suerteId ?? '') : undefined;
    const fincaId = level === 'finca' ? entity.id : suerte?.fincaId;
    return [new Feature({ geometry: boundary, entityId: entity.id, suerteId: suerte?.id, fincaId, fragmentIds, level })];
  });
}

/**
 * Transient display features. Geometry is cached by immutable active model,
 * entity level and DisplaySet; source geometries are cloned and never mutated.
 */
export function buildEntityPresentationFeatures(model: LayerModel, level: Exclude<EntityViewLevel, 'fragment'>, allowedFragmentIds: ReadonlySet<string>): readonly Feature<Geometry>[] {
  let cache = presentationFeatureCache.get(model);
  if (!cache) { cache = new Map(); presentationFeatureCache.set(model, cache); }
  const key = `${level}\0${[...allowedFragmentIds].sort().join('\0')}`;
  const cached = cache.get(key);
  if (cached) return cached;
  const features = entityViewEntities(model, level, allowedFragmentIds).flatMap((entity) => {
    const geometry = entityPresentationGeometry(model, entity);
    if (!geometry) return [];
    const sourceGeometries = entity.fragmentIds.flatMap((id) => {
      const source = model.fragmentsById.get(id)?.feature.getGeometry();
      return source ? [source] : [];
    });
    return [new Feature<Geometry>({
      geometry,
      boundaryGeometry: presentationBoundary(sourceGeometries),
      entityId: entity.id,
      entityLevel: level,
      level,
      fincaId: entity.ids.fincaId,
      suerteId: entity.ids.suerteId,
      tablonId: entity.ids.tablonId,
      fragmentIds: entity.fragmentIds,
    })];
  });
  if (cache.size >= 12) cache.clear();
  cache.set(key, features);
  return features;
}
